/**
 * Person-name validation — the single, framework-free source of truth shared by:
 *   - the web data grid   (web/src/shared/lib/validation/name.ts → validateStrictName)
 *   - the server domain    (server/src/domain/value-objects/person-name.ts → StrictPersonNameSchema)
 *   - the api-client        (api-client/src/common/name.ts → PersonNameStrictSchema)
 *
 * `isValidPersonName` is the exact boolean equivalent of the data grid's
 * `validateStrictName(value).isValid`. The web validator keeps its detailed
 * failure codes but MUST stay byte-identical to the rules below — keep the two
 * in sync when either changes.
 *
 * Rules (a value is a valid person name when ALL hold):
 *   - non-empty after trim
 *   - no digits
 *   - only letters / combining marks / spaces / hyphens
 *   - at most 50 characters (after whitespace normalization)
 *   - at least two words
 *   - each word is at least two characters
 *   - each word contains only letters / combining marks / hyphens
 *   - each word contains at least one actual letter (a word of combining
 *     marks alone — e.g. the Arabic-diacritic-only "ًً" — is not a name)
 * The reserved literal "SYSTEM" (the canonical automated actor) is always valid.
 */

/** Whole-string: letters, combining marks, spaces and hyphens only. */
export const PERSON_NAME_VALID_CHARS_REGEX = /^[\p{L}\p{M}\s-]+$/u;
/** Per-word: letters, combining marks and hyphens only (no spaces). */
export const PERSON_NAME_WORD_VALID_REGEX = /^[\p{L}\p{M}-]+$/u;
/** Per-word: must contain at least one real letter (not marks/hyphens only). */
export const PERSON_NAME_WORD_HAS_LETTER_REGEX = /\p{L}/u;
/** Maximum length after whitespace normalization. */
export const PERSON_NAME_MAX_LENGTH = 50;

const DIGIT_REGEX = /\d/;
const MULTI_SPACE = /\s+/g;
const WORD_SPLIT = /[\s-]+/;

/**
 * Reserved sentinel names that bypass the strict rules. "SYSTEM" is the
 * canonical actor for automated writes (audit trails, migrations, system
 * messages) and must never be flagged as invalid.
 */
export const RESERVED_PERSON_NAMES: ReadonlySet<string> = new Set(["SYSTEM"]);

/** Options for {@link isValidPersonName}. */
export interface PersonNameValidationOptions {
	/**
	 * Minimum number of words required (org-configurable on the calendar name
	 * column: 2 = first + last, 3 = first + middle + last). Clamped to [2, 3];
	 * absent → 2.
	 */
	minWords?: number;
}

/** Clamp a configured word minimum to the supported [2, 3] range. */
export function clampNameMinWords(value: number | null | undefined): number {
	if (typeof value !== "number" || !Number.isFinite(value)) {
		return 2;
	}
	return Math.min(3, Math.max(2, Math.trunc(value)));
}

/**
 * Predicate for whether a string is a valid human person name.
 * Boolean equivalent of the data grid's `validateStrictName(value).isValid`.
 */
export function isValidPersonName(
	value: string | null | undefined,
	options?: PersonNameValidationOptions
): boolean {
	const minWords = clampNameMinWords(options?.minWords);
	const trimmed = (value ?? "").trim();
	if (trimmed.length === 0) {
		return false;
	}
	if (RESERVED_PERSON_NAMES.has(trimmed)) {
		return true;
	}
	if (DIGIT_REGEX.test(trimmed)) {
		return false;
	}
	if (!PERSON_NAME_VALID_CHARS_REGEX.test(trimmed)) {
		return false;
	}
	const normalized = trimmed.replace(MULTI_SPACE, " ").trim();
	if (normalized.length > PERSON_NAME_MAX_LENGTH) {
		return false;
	}
	const words = normalized.split(WORD_SPLIT).filter((w) => w.length > 0);
	if (words.length < minWords) {
		return false;
	}
	if (words.some((w) => w.length < 2)) {
		return false;
	}
	if (words.some((w) => !PERSON_NAME_WORD_VALID_REGEX.test(w))) {
		return false;
	}
	if (words.some((w) => !PERSON_NAME_WORD_HAS_LETTER_REGEX.test(w))) {
		return false;
	}
	return true;
}
